<script setup>
import { computed, defineProps, ref } from 'vue'
import { useToast } from 'vue-toastification'

import api from '@/api'
import eventBus from '@/eventBus'
import { getErrors } from '@/errors'
import { pluralize } from '@/utils'
import useStoredRef from '@/composables/useStoredRef'

const toast = useToast()

const props = defineProps({
  poll: Object
})

const selected = useStoredRef({}, 'concert_polls_selected', props.poll.id)
const selectedArray = computed(() => (
  Object.entries(selected.value).filter(([_, value]) => !!value).map(([key, _]) => Number(key))
))

const toggleSelected = (id) => {
  selected.value[id] = !selected.value[id]
}

const isSubmitting = ref(false)

const canSelect = computed(() => (
  !props.poll.vote && !props.poll.is_locked
))

const totalVotes = computed(() => {
  if (!props.poll.vote) return null

  return props.poll.vote_count
})

const isSelectedCountCorrect = computed(() => (
  selectedArray.value.length === props.poll.required_option_count
))

const canSubmit = computed(() => (
  canSelect.value && props.poll.is_active && isSelectedCountCorrect.value && !isSubmitting.value
))

const submit = () => {
  if (!canSubmit.value) return

  isSubmitting.value = true

  api.post(`/polls/${props.poll.id}/vote/`, { performances: selectedArray.value })
    .then((response) => {
      toast.success('Ваш голос принят')
      eventBus.emit('updatePolls', response.data)
    }, (error) => {
      toast.error(getErrors(error).detail)
    })
    .then(() => {
      isSubmitting.value = false
    })
}
</script>

<template>
  <div
    :class="[
      'poll',
      {'voted': !!poll.vote},
      {'locked': poll.is_locked},
      {'can-select': canSelect}
    ]"
  >
    <div class="poll-header">
      <div class="poll-header-icon">
        <font-awesome-icon
          v-if="poll.is_locked || !poll.is_active"
          icon="lock"
          style="color: var(--red-darker)"
        />
      </div>
      <div class="poll-header-text">
        {{ poll.text }}
      </div>
    </div>
    <div class="poll-option-list">
      <div
        v-for="performance in poll.performances"
        :key="performance.id"
        class="poll-option"
        @click="canSelect && toggleSelected(performance.id)"
      >
        <div class="poll-option-icon">
          <font-awesome-icon
            v-if="canSelect && !!selected[performance.id]"
            :icon="['fas', 'check-circle']"
            style="color: var(--green-darker)"
          />
          <font-awesome-icon
            v-else-if="canSelect"
            :icon="['far', 'circle']"
          />
          <font-awesome-icon
            v-else-if="poll.vote && poll.vote.performances.includes(performance.id)"
            :icon="['fas', 'check-circle']"
            style="color: var(--green-darker)"
          />
        </div>
        <div class="poll-option-text">
          {{ performance.name }}
        </div>
        <div class="poll-option-results">
          <span v-if="poll.vote">
            {{ Math.round(performance.votes / totalVotes * 100) }}%
          </span>
        </div>
        <div
          class="poll-option-line"
          :style="{
            width: poll.vote ? `${Math.round(performance.votes / totalVotes * 100)}%` : 0
          }"
        ></div>
      </div>
    </div>
    <div class="poll-buttons">
      <button
        v-if="canSelect"
        class="button no-margin"
        :disabled="!canSubmit"
        @click="submit"
      >
        <template v-if="!poll.is_active">
          Голосование ещё не открыто
        </template>
        <template v-else-if="!isSelectedCountCorrect">
          Выберите {{ poll.required_option_count }} {{ $utils.pluralize(poll.required_option_count, 'вариант', 'варианта', 'вариантов') }}
        </template>
        <template v-else>
          Проголосовать
        </template>
      </button>
    </div>
    <div class="total-votes">
      {{ `${totalVotes} ${pluralize(totalVotes, 'голос', 'голоса', 'голосов')}` }}
    </div>
  </div>
</template>

<style scoped>
.poll {
  margin-bottom: 2em;
}

.poll.locked {
  opacity: 0.5;
}

.poll-header {
  display: flex;
  align-items: center;
  gap: 0.6em;

  margin-bottom: 0.5em;
  font-weight: bold;
}

.poll-header-icon {
  width: 1.2em;
}

.poll-header-text {
  flex: 1;
}

.poll-option-list {
  margin-bottom: 0.5em;
}

.poll-option {
  display: flex;
  gap: 0.6em;

  padding: 0.8em 0.5em;
  margin: 0 -0.5em;

  position: relative;
}

.poll.can-select .poll-option {
  cursor: pointer;
}

.poll:not(.locked) .poll-option:hover {
  background-color: var(--light-overlay);
}

.poll-option-icon {
  width: 1.2em;
}

.poll-option-text {
  flex: 1;
}

.poll-option-results {
  width: 3em;
  text-align: right;
}

.poll-option-line {
  position: absolute;
  bottom: 0.5em;
  left: 2.3em;

  height: 2px;

  background-color: var(--green);

  transition: width 0.2s;
}

.poll-buttons {
  display: flex;
  flex-direction: column;
}

.total-votes {
  text-align: right;
  visibility: hidden;
}

.poll.voted .total-votes {
  visibility: visible;
}
</style>
